// @flow

import React, { PureComponent } from 'react';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { i18n, Analytics } from 'shared/utils';
import styled from 'styled-components';
import { QueryRenderer, graphql } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import {
  Formik,
  Form,
  FieldGroup,
  FormAutoSaver,
} from 'shared/components/form';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import Divider from 'shared/components/common/Divider';
import { colors } from 'shared/styleguide';
import { Link } from 'react-router-dom';
import UserAvatar from 'shared/components/common/UserAvatar';
import UpdateViewerAvatarModal from 'main-app/components/UpdateViewerAvatarModal';
import UpdateViewerPasswordModal from 'main-app/components/UpdateViewerPasswordModal';
import UpdateViewerMutation from 'main-app/mutations/UpdateViewer';
import UpdateCompanyMutation from 'main-app/mutations/UpdateCompany';
import TwoFactorProfileSection from 'main-app/components/TwoFactorProfileSection';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import * as Actions from 'main-app/store/Actions';

type Props = {};

type State = {
  updateViewerAvatarOpen: boolean,
  updateViewerPasswordOpen: boolean,
  ssoLoading: boolean,
  ssoJustEnabled: boolean,
  profileRefreshKey: number,
};

const DividerWrapper = styled(Divider)`
  width: 300px;
  height: 2px;
  border-radius: 1px;
  background-color: ${colors.lightBlueGrey};
  margin-bottom: 32px;
`;

const RoleWrapper = styled.div`
  span {
    display: block;
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${colors.charcoalGrey};
  }

  span:first-of-type {
    margin-bottom: 16px;
  }

  span:last-of-type {
    height: 21px;
    font-size: 16px;
  }
`;

const LinkUpdateAvatarWrapper = styled.div`
  margin: 16px 0 24px 0;
`;

const LinkUpdatePasswordWrapper = styled.span`
  margin: 0 0 32px 0;
`;

const SpanText = styled.span`
  font-size: 14px;
  display: inline-block;
`;

const TIMEZONE_OPTIONS = moment.tz.names().map(tz => ({
  label: tz,
  value: tz,
}));

class Profile extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      updateViewerAvatarOpen: false,
      updateViewerPasswordOpen: false,
      ssoLoading: false,
      ssoJustEnabled: false,
      profileRefreshKey: 0,
    };
  }

  handleUpdateViewerPasswordOpenModal = () => {
    this.setState({ updateViewerPasswordOpen: true });
  };

  handleUpdateViewerAvatarOpenModal = () => {
    this.setState({ updateViewerAvatarOpen: true });
  };

  handleCloseAllModals = () => {
    this.setState({
      updateViewerAvatarOpen: false,
      updateViewerPasswordOpen: false,
    });
  };

  handleEnableSSO = async () => {
    this.setState({ ssoLoading: true });
    try {
      const resp = await fetch('/oauth/keycloak/provision-sso', {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to enable SSO');
      }
      this.setState({ ssoJustEnabled: true });
      Actions.alertNotification(
        i18n.t('SSO enabled! You can now sign in with Google.'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    } finally {
      this.setState({ ssoLoading: false });
    }
  };

  render() {
    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        variables={null}
        key={this.state.profileRefreshKey}
        query={graphql`
          query ProfileQuery {
            viewer {
              id
              avatar
              firstName
              lastName
              email
              ssoProvider
              role
              phone
              phoneVerified
              twoFactorEnabled
              company {
                id
                timezone
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
              ...UserAvatar_user
              ...UpdateViewerAvatarModal_user
            }
          }
        `}
        render={query => {
          if (!query.props) {
            return <Loader />;
          }
          const currentUser = query.props.viewer;
          const {
            updateViewerAvatarOpen,
            updateViewerPasswordOpen,
            ssoLoading,
            ssoJustEnabled,
          } = this.state;
          const ssoEnabled = ssoJustEnabled || currentUser.ssoProvider === 'keycloak';

          return (
            <>
              <UserAvatar width={60} user={currentUser} />
              <LinkUpdateAvatarWrapper>
                <Link to="#" onClick={this.handleUpdateViewerAvatarOpenModal}>
                  <SpanText>{i18n.t('Update Avatar')}</SpanText>
                </Link>
              </LinkUpdateAvatarWrapper>
              <Formik
                initialValues={{
                  firstName: currentUser.firstName,
                  lastName: currentUser.lastName,
                  email: currentUser.email,
                  password: '',
                }}
                validationSchema={Yup.object().shape({
                  firstName: Yup.string()
                    .trim()
                    .required('Required'),
                  lastName: Yup.string()
                    .trim()
                    .required('Required'),
                  email: Yup.string()
                    .email('Please Enter A Valid Email')
                    .required('Required'),
                })}
                onSubmit={async (values: *, { setSubmitting }: *) => {}}
                render={({
                  values,
                  errors,
                  isValid,
                  isSubmitting,
                  handleSubmit,
                  setFieldValue,
                  handleBlur,
                  validateForm,
                }) => {
                  return (
                    <>
                      <FormAutoSaver
                        onSave={async values => {
                          if (!isValid) {
                            return;
                          }

                          try {
                            await UpdateViewerMutation.commit({
                              variables: {
                                input: {
                                  firstName: values.firstName.trim(),
                                  lastName: values.lastName.trim(),
                                  email: values.email.trim(),
                                },
                              },
                            });
                            Analytics.trackEvent('Update Profile', {
                              firstName: values.firstName.trim(),
                              lastName: values.lastName.trim(),
                              email: values.email.trim(),
                            });
                            Actions.alertNotification(
                              i18n.t('Your Profile Has Been Updated!'),
                              'success',
                            );
                          } catch (e) {
                            Actions.alertNotification(e.message, 'error');
                          }
                        }}
                      />
                      <Form>
                        <FieldGroup
                          name="firstName"
                          label={i18n.t('First Name')}
                          size="small"
                          error={errors.firstName}
                        />
                        <FieldGroup
                          name="lastName"
                          label={i18n.t('Last Name')}
                          size="small"
                          error={errors.lastName}
                        />
                        <FieldGroup
                          name="email"
                          label={i18n.t('Email')}
                          size="small"
                          error={errors.email}
                        />
                        <FieldGroup
                          name="password"
                          type="password"
                          label={i18n.t('Password')}
                          size="small"
                          disabled
                        />
                      </Form>
                    </>
                  );
                }}
              />
              <LinkUpdatePasswordWrapper>
                <Link to="#" onClick={this.handleUpdateViewerPasswordOpenModal}>
                  <SpanText>{i18n.t('Update Password')}</SpanText>
                </Link>
              </LinkUpdatePasswordWrapper>
              <div style={{ margin: '24px 0' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 'bold', marginBottom: 12 }}>
                  {i18n.t('Single Sign-On')}
                </span>
                {ssoEnabled ? (
                  <span style={{ fontSize: 14, color: '#4caf50' }}>
                    {i18n.t('SSO Enabled — you can sign in with Google')}
                  </span>
                ) : (
                  <Button
                    theme="border"
                    loading={ssoLoading}
                    onClick={this.handleEnableSSO}
                  >
                    {i18n.t('Enable Sign in with Google')}
                  </Button>
                )}
              </div>
              <TwoFactorProfileSection
                role={currentUser.role}
                twoFactorEnabled={currentUser.twoFactorEnabled}
                phoneVerified={currentUser.phoneVerified}
                phone={currentUser.phone}
                onUpdated={message => {
                  Actions.alertNotification(
                    message || i18n.t('Phone number verified successfully.'),
                    'success',
                  );
                  this.setState(({ profileRefreshKey }) => ({
                    profileRefreshKey: profileRefreshKey + 1,
                  }));
                }}
                onError={message => Actions.alertNotification(message, 'error')}
              />
              <DividerWrapper />
              <RoleWrapper>
                <span>{i18n.t('Company Settings')}</span>
              </RoleWrapper>
              <Formik
                enableReinitialize
                initialValues={{
                  timezone: TIMEZONE_OPTIONS.find(
                    o => o.value === currentUser.company.timezone,
                  ) || '',
                  addressLine1: currentUser.company.addressLine1 || '',
                  addressLine2: currentUser.company.addressLine2 || '',
                  addressCity: currentUser.company.addressCity || '',
                  addressState: currentUser.company.addressState || '',
                  addressZip: currentUser.company.addressZip || '',
                  addressCountry: currentUser.company.addressCountry || '',
                  phone: currentUser.company.phone || '',
                }}
                onSubmit={async (values, { setSubmitting }) => {
                  const tz =
                    values.timezone && typeof values.timezone === 'object'
                      ? values.timezone.value
                      : values.timezone;
                  try {
                    await UpdateCompanyMutation.commit({
                      variables: {
                        input: {
                          timezone: tz || null,
                          addressLine1: values.addressLine1 || null,
                          addressLine2: values.addressLine2 || null,
                          addressCity: values.addressCity || null,
                          addressState: values.addressState || null,
                          addressZip: values.addressZip || null,
                          addressCountry: values.addressCountry || null,
                          phone: values.phone || null,
                        },
                      },
                    });
                    Actions.alertNotification(
                      i18n.t('Company settings updated'),
                      'success',
                    );
                  } catch (e) {
                    Actions.alertNotification(e.message, 'error');
                  }
                  setSubmitting(false);
                }}
                render={({ values, isSubmitting, handleSubmit, setFieldValue }) => (
                  <Form>
                    <FieldGroup
                      name="timezone"
                      label={i18n.t('Timezone')}
                      type="select"
                      size="small"
                      options={TIMEZONE_OPTIONS}
                      placeholder={i18n.t('Select a timezone...')}
                      isSearchable
                    />
                    <AddressAutocomplete
                      value={values.addressLine1 || ''}
                      label={i18n.t('Address Line 1')}
                      onChange={val => setFieldValue('addressLine1', val)}
                      onAddressSelect={({ line1, city, state, zip }) => {
                        if (line1) setFieldValue('addressLine1', line1);
                        if (city) setFieldValue('addressCity', city);
                        if (state) setFieldValue('addressState', state);
                        if (zip) setFieldValue('addressZip', zip);
                      }}
                    />
                    <FieldGroup
                      name="addressLine2"
                      label={i18n.t('Address Line 2')}
                      size="small"
                    />
                    <FieldGroup
                      name="addressCity"
                      label={i18n.t('City')}
                      size="small"
                    />
                    <FieldGroup
                      name="addressState"
                      label={i18n.t('State / Province')}
                      size="small"
                    />
                    <FieldGroup
                      name="addressZip"
                      label={i18n.t('ZIP / Postal Code')}
                      size="small"
                    />
                    <FieldGroup
                      name="addressCountry"
                      label={i18n.t('Country')}
                      size="small"
                    />
                    <FieldGroup
                      name="phone"
                      label={i18n.t('Phone')}
                      size="small"
                    />
                    <Button
                      theme="blue"
                      loading={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {i18n.t('Save Company Settings')}
                    </Button>
                  </Form>
                )}
              />
              {updateViewerAvatarOpen && (
                <UpdateViewerAvatarModal
                  onClose={this.handleCloseAllModals}
                  user={currentUser}
                />
              )}
              {updateViewerPasswordOpen && (
                <UpdateViewerPasswordModal
                  onClose={this.handleCloseAllModals}
                />
              )}
            </>
          );
        }}
      />
    );
  }
}

export default Profile;
